// Purpose: Handle multiple aspects of the header dropdown related to accessibility and keyboard navigation.
// Keyboard caret: Shows a keyboard-only caret (js-header-a11y-caret) when navigating with Tab.
// Mouse behavior: Opens/hides dropdowns on mouseenter/click without showing the caret.
// ARIA/state: Toggles aria-expanded and is-open on parent .Header-title.
// Focus handling: Opens submenu, focuses first item, and closes on Escape.
// Input tracking: Uses lastInputWasKeyboard to distinguish keyboard vs pointer input.
export const init = async function() {
    let isLinkHovered = false;
    let lastInputWasKeyboard = false;
    // Track input modality so we only show the keyboard-specific caret when
    // the user is navigating with the keyboard (not when clicking with mouse)
    document.addEventListener('keydown', ()=>{
        lastInputWasKeyboard = true;
    }, {
        capture: true
    });
    document.addEventListener('pointerdown', ()=>{
        lastInputWasKeyboard = false;
    }, {
        capture: true
    });
    document.querySelectorAll('.js-header-link').forEach((link)=>{
        const linkEl = link;
        const showDropdown = (target)=>{
            const parent = target.parentElement;
            if (parent) {
                // Close any other open dropdowns first
                document.querySelectorAll('.Header-title.is-open').forEach((openParent)=>{
                    if (openParent !== parent) {
                        openParent.classList.remove('is-open');
                        const openLink = openParent.querySelector('.js-header-link');
                        if (openLink) openLink.setAttribute('aria-expanded', 'false');
                    }
                });
                const dropdown = parent.querySelector('.js-header-dropdown');
                if (dropdown) {
                    target.setAttribute('aria-expanded', 'true');
                    parent.classList.add('is-open');
                }
            }
        };
        const hideDropdown = (target)=>{
            const parent = target.parentElement;
            if (parent) {
                const dropdown = parent.querySelector('.js-header-dropdown');
                if (dropdown && !isLinkHovered) {
                    target.setAttribute('aria-expanded', 'false');
                    parent.classList.remove('is-open');
                }
            }
        };
        // Force-close dropdown regardless of hover state (used for Escape)
        const closeDropdown = (target)=>{
            const parent = target.parentElement;
            if (parent) {
                const dropdown = parent.querySelector('.js-header-dropdown');
                if (dropdown) {
                    target.setAttribute('aria-expanded', 'false');
                    parent.classList.remove('is-open');
                }
            }
        };
        link.addEventListener('mouseenter', (event)=>{
            const target = event.target;
            isLinkHovered = true;
            if (target instanceof HTMLElement) {
                showDropdown(target);
            }
        });
        link.addEventListener('mouseleave', (event)=>{
            const target = event.target;
            isLinkHovered = false;
            if (target instanceof HTMLElement) {
                setTimeout(()=>{
                    hideDropdown(target);
                }, 10);
            }
        });
        // Create a caret button that appears when the link is focused (keyboard tab)
        const parentEl = linkEl.parentElement;
        if (parentEl) {
            const dropdown = parentEl.querySelector('.js-header-dropdown');
            if (dropdown) {
                // create caret element once
                let caret = parentEl.querySelector('.js-header-a11y-caret');
                if (!caret) {
                    caret = document.createElement('button');
                    // match the SCSS selector (.js-header-a11y-caret)
                    caret.className = 'js-header-a11y-caret';
                    caret.type = 'button';
                    caret.setAttribute('aria-label', 'Open submenu');
                    caret.setAttribute('tabindex', '0');
                    caret.style.display = 'none';
                    // visually minimal; styling provided in CSS
                    linkEl.insertAdjacentElement('afterend', caret);
                    // caret keyboard handlers
                    caret.addEventListener('keydown', (e)=>{
                        const ke = e;
                        if (ke.key === 'Enter' || ke.key === ' ' || ke.key === 'Spacebar') {
                            ke.preventDefault();
                            showDropdown(linkEl);
                            // focus first focusable inside dropdown
                            const first = dropdown.querySelector('a, button, [tabindex]');
                            if (first) first.focus();
                        } else if (ke.key === 'Escape') {
                            // force close and return focus
                            closeDropdown(linkEl);
                            linkEl.focus();
                        }
                    });
                    // clicking caret should open dropdown too
                    caret.addEventListener('click', (e)=>{
                        e.preventDefault();
                        showDropdown(linkEl);
                    });
                }
                // show caret when link or caret is focused; hide on blur
                const showCaret = ()=>{
                    // Only show caret when the most recent input was from keyboard
                    if (!lastInputWasKeyboard) return;
                    if (caret) caret.style.display = '';
                    // ensure focusing the link does not open the dropdown
                    if (linkEl instanceof HTMLElement) {
                        linkEl.setAttribute('aria-expanded', 'false');
                        // explicitly close any open dropdown for this parent
                        closeDropdown(linkEl);
                    }
                };
                const hideCaretIfNeeded = ()=>{
                    setTimeout(()=>{
                        const active = document.activeElement;
                        if (active !== linkEl && active !== caret) {
                            if (caret) caret.style.display = 'none';
                        }
                    }, 10);
                };
                linkEl.addEventListener('focus', showCaret);
                linkEl.addEventListener('blur', hideCaretIfNeeded);
                if (caret) {
                    caret.addEventListener('focus', showCaret);
                    caret.addEventListener('blur', hideCaretIfNeeded);
                }
            }
        }
        const parent = link.parentElement;
        if (parent) {
            const dropdown = parent.querySelector('.js-header-dropdown');
            if (dropdown) {
                dropdown.addEventListener('mouseenter', ()=>{
                    isLinkHovered = true;
                    if (link instanceof HTMLElement) {
                        showDropdown(link);
                    }
                });
                dropdown.addEventListener('mouseleave', ()=>{
                    isLinkHovered = false;
                    if (link instanceof HTMLElement) {
                        setTimeout(()=>{
                            hideDropdown(link);
                        }, 10);
                    }
                });
                // close dropdown on Escape and return focus to parent link
                dropdown.addEventListener('keydown', (e)=>{
                    const ke = e;
                    if (ke.key === 'Escape') {
                        ke.preventDefault();
                        const parentTitle = dropdown.closest('.Header-title');
                        if (parentTitle) {
                            const parentLink = parentTitle.querySelector('.js-header-link');
                            if (parentLink) {
                                closeDropdown(parentLink);
                                parentLink.focus();
                            }
                        }
                    }
                });
            }
        }
        link.addEventListener('mouseenter', ()=>{
            document.querySelectorAll('.js-header-link').forEach((otherLink)=>{
                if (otherLink !== link) {
                    otherLink.setAttribute('aria-expanded', 'false');
                    const otherParent = otherLink.closest('.Header-title');
                    if (otherParent) otherParent.classList.remove('is-open');
                }
            });
        });
    });
};
